<div class="company-verification-report">
  <PageControl
    :title="'Company Account Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      @click="show_export_modal"
    >
      <i class="fas fa-download me-1"></i> Export Data
    </button>

    <!-- Export Modal -->
    <Teleport to="body">
      <div
        class="modal fade"
        :id="'export-modal-' + report_reference"
        tabindex="-1"
        aria-labelledby="exportModalLabel"
        aria-hidden="true"
      >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export Company Account Verification Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-company-info"
                  v-model="export_options.company_information"
                />
                <label class="form-check-label" for="export-company-info">
                  <strong>Company Information</strong>
                  <small class="d-block text-muted"
                    >Company details, registration number, bank information, and overall verification result</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-verification-results"
                  v-model="export_options.verification_results"
                />
                <label class="form-check-label" for="export-verification-results">
                  <strong>Verification Results</strong>
                  <small class="d-block text-muted"
                    >Individual verification checks and their outcomes</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-transaction-details"
                  v-model="export_options.transaction_details"
                />
                <label class="form-check-label" for="export-transaction-details">
                  <strong>Transaction Details</strong>
                  <small class="d-block text-muted"
                    >Enquiry information, reference numbers, and processing details</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-raw-json"
                  v-model="export_options.raw_json"
                />
                <label class="form-check-label" for="export-raw-json">
                  <strong>Raw JSON Data</strong>
                  <small class="d-block text-muted"
                    >Original unprocessed data from the company account verification</small
                  >
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-csv"
                  value="csv"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-csv">
                  CSV (Separate files for each table)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-excel"
                  value="excel"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-excel">
                  Excel (Multiple sheets in one file)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-json"
                  value="json"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-json">
                  JSON (JavaScript Object Notation)
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
              :disabled="!has_selected_tables"
            >
              <i class="fas fa-download me-1"></i>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </PageControl>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-2 header d-flex align-items-center justify-content-between border-bottom mb-2"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h5 fw-bold mb-1">Company Account Verification Report</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm p-2 bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-building me-2"></i>
            Company Account Verification Summary
          </h6>
        </div>
        <div class="card-body">
          <p>
            This report summarizes the company account verification for
            <span class="fw-bold text-dark">{{ company_name }}</span> (Registration Number:
            <span class="fw-bold text-dark">{{ registration_number }}</span>), for account number
            <span class="fw-bold text-dark">{{ account_number }}</span> at branch code
            <span class="fw-bold text-dark">{{ branch_number }}</span>
            (Reference ID: <span class="fw-bold text-dark">{{ client_user_reference }}</span>).
          </p>
        </div>
      </div>

      <!-- Report Header -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-building-user me-2"></i>
                Company Account Verification Report
              </h6>
              <div class="text-muted small">
                Reference: {{ client_user_reference }} | Generated: {{ formatted_current_date }}
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Company Name:</span>
                    <div class="fw-semibold">{{ company_name }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Registration Number:</span>
                    <div class="fw-semibold">{{ registration_number }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Account Number:</span>
                    <div class="fw-semibold">{{ account_number }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern mb-2">Verification Status:</span>
                    <div>
                      <span :class="verification_status_badge_class" class="status-badge">
                        <i :class="verification_status_icon" class="me-1"></i>
                        {{ overall_verification_status }}
                      </span>
                    </div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Branch Code:</span>
                    <div class="fw-semibold">{{ branch_number }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Account Type:</span>
                    <div class="fw-semibold">{{ account_type_description }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-clipboard-check me-2"></i>
                Verification Summary
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Account Type Code:</span>
                    <div class="fw-semibold">{{ account_type }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Record Indicator:</span>
                    <div class="fw-semibold">{{ record_indicator }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Sub Billing ID:</span>
                    <div class="fw-semibold">{{ sub_billing_id }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Client Reference:</span>
                    <div class="fw-semibold">{{ client_user_reference }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>
    
    <SectionSheet>
      <!-- Verification Results Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-shield-alt me-2"></i>
                Company Account Verification Results
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Verification Check</th>
                      <th class="text-xs">Description</th>
                      <th class="text-xs">Result</th>
                      <th class="text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="fw-semibold text-xs">Account Found</td>
                      <td class="text-xs">Verify if account exists in banking system</td>
                      <td class="text-xs">{{ account_found }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(account_found)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(account_found)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(account_found) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Registration Number Match</td>
                      <td class="text-xs">Verify if registration number matches account holder</td>
                      <td class="text-xs">{{ id_number_match }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(id_number_match)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(id_number_match)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(id_number_match) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Company Name Match</td>
                      <td class="text-xs">Verify if company name matches account holder</td>
                      <td class="text-xs">{{ surname_match }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(surname_match)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(surname_match)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(surname_match) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Account Open</td>
                      <td class="text-xs">Verify if account is currently active</td>
                      <td class="text-xs">{{ account_open }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(account_open)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(account_open)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(account_open) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Account Age (3+ Months)</td>
                      <td class="text-xs">Verify if account has been open for at least 3 months</td>
                      <td class="text-xs">{{ account_open_for_atleast_three_months }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(account_open_for_atleast_three_months)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(account_open_for_atleast_three_months)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(account_open_for_atleast_three_months) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Accepts Debits</td>
                      <td class="text-xs">Verify if account can receive debit transactions</td>
                      <td class="text-xs">{{ account_accepts_debits }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(account_accepts_debits)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(account_accepts_debits)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(account_accepts_debits) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Email Match</td>
                      <td class="text-xs">Verify if email address matches account records</td>
                      <td class="text-xs">{{ email_match }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(email_match)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(email_match)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(email_match) }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold text-xs">Account Type Return</td>
                      <td class="text-xs">Verify if account type information was returned</td>
                      <td class="text-xs">{{ account_type_return }}</td>
                      <td class="text-xs">
                        <span
                          :class="get_verification_badge_class(account_type_return)"
                          class="badge"
                        >
                          <i
                            :class="get_verification_icon(account_type_return)"
                            class="me-1"
                          ></i>
                          {{ get_verification_status_text(account_type_return) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Contact Information Section -->
      <div class="row mb-4" v-if="email_address">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-address-book me-2"></i>
                Contact Information
              </h6>
            </div>
            <div class="card-body">
              <!-- Email Address -->
              <div class="row mb-3">
                <div class="col-12">
                  <span class="label-modern">Email Address:</span>
                  <div class="fw-semibold pt-2 text-xs">
                    {{ email_address }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <!-- No Contact Information Message -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-header">
                <h6 class="mb-0">
                  <i class="fas fa-address-book me-2"></i>
                  Contact Information
                </h6>
              </div>
              <div class="card-body">
                <div class="text-center p-4">
                  <i class="fas fa-info-circle fa-3x mb-1 text-info"></i>
                  <h6 class="fw-semibold">No Contact Information Available</h6>
                  <p class="text-muted mb-0">
                    There are no contact details available for this company account verification.
                    Contact information may not have been provided in the original request.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Account Details -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-info-circle me-2"></i>
                Additional Account Details
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive mt-2">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Field</th>
                      <th class="text-xs">Value</th>
                      <th class="text-xs">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-xs fw-semibold">Sequence Number</td>
                      <td class="text-xs">{{ sequence_number }}</td>
                      <td class="text-xs">Transaction sequence identifier</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">ID Type</td>
                      <td class="text-xs">{{ id_type }}</td>
                      <td class="text-xs">Type of identification document</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Tax Reference Match</td>
                      <td class="text-xs">{{ tax_reference_match }}</td>
                      <td class="text-xs">Tax reference number verification status</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Account Dormant</td>
                      <td class="text-xs">{{ account_dormant }}</td>
                      <td class="text-xs">Status of account dormancy</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Account Issuer</td>
                      <td class="text-xs">{{ account_issuer }}</td>
                      <td class="text-xs">Institution that issued the account</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Error Condition</td>
                      <td class="text-xs">{{ error_condition_number }}</td>
                      <td class="text-xs">Any error conditions during verification</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <div class="card shadow-sm">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Disclaimer
          </h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-info-circle me-2"></i>General Information
            </h6>
            <p class="text-xs mb-4">
              This Company Account Verification Report is prepared solely for the benefit
              of the intended recipient and is based on data obtained from authorized banking
              institutions. While <strong> THE GOOD PEOPLE DATA COMPANY (PTY) LTD ('TGPDC')
                </strong> employs industry-standard practices to collect and process information,
              TGPDC does not guarantee, and expressly disclaims, the accuracy, completeness
              or timeliness of any data contained herein. The Report reflects the
              status of each record at the moment of generation and may not
              reflect subsequent changes.
            </p>
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-shield-lock me-2"></i>Data Privacy
            </h6>
            <p class="text-xs mb-4">
              All company and financial information is processed in accordance with
              applicable data protection laws including POPIA (Protection of
              Personal Information Act). This report should only be shared with
              authorized personnel who require access to this information for
              legitimate business purposes.
            </p>
            <div class="text-center text-muted mt-3">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company.
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>